// src/RefertoFieldRenderer.js
//
// ============================================================================
// SCHEMA CONDIVISO MODELLI REFERTO — fonte di verità
// ============================================================================
// Questo blocco di commento è IDENTICO nei due progetti:
//   - Master HR:          src/RefertoFieldRenderer.js   (questo file)
//   - Gestionale Clinico:  src/components/RefertoFieldRenderer.jsx
// Non potendo condividere codice tra i due repository, ogni modifica allo
// schema dei campi (nuovi type, nuove proprietà) VA APPLICATA IN ENTRAMBI
// i file, altrimenti un modello creato in HR si comporta diversamente
// quando viene compilato/stampato nel clinico.
//
// Un "template" (centers/{centerId}/refertoTemplates/{templateId}) ha:
//   { nome, descrizione, version, fields: Field[], updatedAt, updatedBy }
//
// Ogni Field ha questa forma:
//   {
//     id: string,              // es. "fld_1234567890", univoco nel template
//     type: FieldType,         // vedi elenco sotto
//     label: string,           // etichetta mostrata
//     required: boolean,       // blocca la firma del referto se vuoto
//     patientVisible: boolean, // se false, il campo è escluso dal PDF inviato
//                               // al paziente ma resta nello storico interno
//     placeholder: string,     // solo per 'text' / 'textarea'
//     defaultValue: string,    // valore precompilato (testo pre-scritto modificabile)
//     options: string,         // CSV "Opzione 1, Opzione 2" — solo select_single/select_multi
//     viewLabel: string,       // etichetta vista (es. "Anteriore") — solo 'bodychart'
//     visibleIf: {             // campo condizionale, oppure null
//       fieldId: string,       // id di un altro campo (solo checkbox o select_single)
//       equals: string,        // valore atteso per mostrare questo campo
//     } | null,
//   }
//
// FieldType ammessi:
//   - 'section'        Separatore/intestazione di sezione, nessun valore associato.
//   - 'text'            Input testo singola riga.
//   - 'textarea'         Area di testo multi-riga.
//   - 'select_single'   Tendina a scelta singola (radio/select). options = CSV.
//   - 'select_multi'    Tendina a scelta multipla (checkbox group). options = CSV.
//                       Il valore compilato è un array di stringhe.
//   - 'checkbox'        Singola checkbox booleana.
//   - 'vas'              Scala del dolore 0-10. Valore compilato: number.
//   - 'bodychart'        Corpo umano interattivo. Valore compilato: array di
//                       marker { x, y, note? } in coordinate percentuali (0-100)
//                       relative all'immagine, per restare indipendenti dalla
//                       risoluzione di rendering nei due progetti.
//
// I valori compilati di un referto sono salvati come mappa { [fieldId]: valore }
// insieme a un templateSnapshot (= copia congelata di template.fields al momento
// della compilazione), MAI un riferimento "live" al template, per garantire che
// i referti vecchi restino leggibili identici a come sono stati firmati anche
// se il template viene modificato/eliminato in seguito.
// ============================================================================

import React from 'react';

const inputStyle = { width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', marginBottom: '4px' };

// Valuta se un campo va mostrato in base alla regola visibleIf e ai valori correnti
function isFieldVisible(field, allFields, values) {
    if (!field.visibleIf || !field.visibleIf.fieldId) return true;
    const sourceField = allFields.find(f => f.id === field.visibleIf.fieldId);
    if (!sourceField) return true;
    const sourceValue = values[field.visibleIf.fieldId];
    if (sourceField.type === 'checkbox') {
        return field.visibleIf.equals === 'true' ? !!sourceValue : !sourceValue;
    }
    return sourceValue === field.visibleIf.equals;
}

// Componente semplificato di anteprima del corpo umano (solo per HR — preview).
// Nel gestionale clinico la versione reale userà il componente BodyChart già esistente.
function BodyChartPreview({ markers = [], viewLabel, readOnly, onAddMarker, onRemoveMarker }) {
    return (
        <div style={{ textAlign: 'center' }}>
            {viewLabel && <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '6px', fontWeight: '600' }}>{viewLabel}</div>}
            <div
                onClick={readOnly ? undefined : (e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    onAddMarker && onAddMarker({ x, y });
                }}
                style={{
                    position: 'relative', width: '140px', height: '280px', margin: '0 auto',
                    backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '70px / 30px',
                    cursor: readOnly ? 'default' : 'crosshair',
                }}
                title={readOnly ? '' : 'Clicca per aggiungere un marker (anteprima)'}
            >
                {markers.map((m, idx) => (
                    <span
                        key={idx}
                        onClick={(e) => { e.stopPropagation(); !readOnly && onRemoveMarker && onRemoveMarker(idx); }}
                        style={{
                            position: 'absolute', left: `${m.x}%`, top: `${m.y}%`,
                            width: '10px', height: '10px', borderRadius: '50%',
                            backgroundColor: '#ef4444', transform: 'translate(-50%, -50%)',
                            cursor: readOnly ? 'default' : 'pointer',
                        }}
                    />
                ))}
            </div>
            {!readOnly && <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '4px' }}>Anteprima — clic per simulare un marker</div>}
        </div>
    );
}

/**
 * RefertoFieldRenderer
 *
 * Renderizza un singolo campo del template in una delle modalità:
 *   - 'edit'     campo modificabile (usato nel gestionale clinico durante la compilazione)
 *   - 'readonly' campo bloccato, mostra solo il valore (referto firmato / stampa)
 *   - 'preview'  modalità builder (Master HR): mostra il campo disabilitato,
 *                serve solo a far vedere a chi crea il template come apparirà
 *
 * Props:
 *   field        Field (vedi schema sopra)
 *   allFields    Field[] — tutti i campi del template, serve per valutare visibleIf
 *   value        valore corrente del campo (ignorato in modalità 'preview')
 *   onChange     (newValue) => void — ignorato se mode !== 'edit'
 *   mode         'edit' | 'readonly' | 'preview'
 */
export default function RefertoFieldRenderer({ field, allFields = [], value, onChange, mode = 'preview', redFlagsOptions = [] }) {
    const values = {}; // in preview/readonly statico non serve risolvere visibleIf su dati reali
    if (mode === 'edit' && allFields.length) {
        allFields.forEach(f => { values[f.id] = f.id === field.id ? value : undefined; });
    }

    // In preview mostriamo sempre tutti i campi (anche quelli condizionali), per dare
    // al medico/admin che costruisce il template una visione completa.
    if (mode !== 'preview' && !isFieldVisible(field, allFields, values)) return null;

    const isReadOnly = mode === 'readonly' || mode === 'preview';
    const fieldOptions = (field.options || '').split(',').map(o => o.trim()).filter(Boolean);

    if (field.type === 'section') {
        return (
            <div style={{ gridColumn: '1 / -1', borderBottom: '2px solid #e5e7eb', paddingBottom: '4px', marginTop: '10px' }}>
                <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem' }}>{field.label}</h4>
            </div>
        );
    }

    return (
        <div style={{ opacity: mode === 'preview' ? 0.9 : 1 }}>
            <label style={labelStyle}>
                {field.label}{field.required && <span style={{ color: '#dc2626' }}> *</span>}
                {!field.patientVisible && <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: '#9ca3af' }}>(uso interno)</span>}
            </label>

            {field.type === 'text' && (
                <input type="text" value={value || ''} placeholder={field.placeholder} readOnly={isReadOnly}
                    onChange={e => onChange && onChange(e.target.value)} style={inputStyle} />
            )}

            {field.type === 'textarea' && (
                <textarea value={value || ''} placeholder={field.placeholder} readOnly={isReadOnly}
                    onChange={e => onChange && onChange(e.target.value)} style={{ ...inputStyle, height: '80px', resize: 'vertical' }} />
            )}

            {field.type === 'select_single' && (
                <select value={value || ''} disabled={isReadOnly} onChange={e => onChange && onChange(e.target.value)} style={inputStyle}>
                    <option value="">— seleziona —</option>
                    {fieldOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            )}

            {field.type === 'select_multi' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                    {fieldOptions.map(opt => {
                        const checked = Array.isArray(value) && value.includes(opt);
                        return (
                            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem', cursor: isReadOnly ? 'default' : 'pointer' }}>
                                <input type="checkbox" checked={checked} disabled={isReadOnly}
                                    onChange={() => {
                                        if (!onChange) return;
                                        const current = Array.isArray(value) ? value : [];
                                        onChange(checked ? current.filter(v => v !== opt) : [...current, opt]);
                                    }} />
                                {opt}
                            </label>
                        );
                    })}
                </div>
            )}

            {field.type === 'red_flags' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(redFlagsOptions.length ? redFlagsOptions : ['Pace-maker', 'Anticoagulanti', 'Gravidanza', 'Allergie', 'Diabete', 'Ipertensione']).filter(flag => !(field.excludedFlags || []).includes(flag)).map(flag => (
                        <span key={flag} style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 'bold', backgroundColor: 'white', color: '#be123c', border: '1px solid #fecdd3' }}>
                            + {flag}
                        </span>
                    ))}
                </div>
            )}

            {field.type === 'checkbox' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isReadOnly ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={!!value} disabled={isReadOnly} onChange={e => onChange && onChange(e.target.checked)} />
                    <span style={{ fontSize: '0.85rem', color: '#374151' }}>{value ? 'Sì' : 'No'}</span>
                </label>
            )}

            {field.type === 'vas' && (
                <div>
                    <span style={{ fontWeight: 'bold', color: (value || 0) > 7 ? '#ef4444' : (value || 0) > 3 ? '#eab308' : '#22c55e' }}>{value || 0} / 10</span>
                    <input type="range" min="0" max="10" value={value || 0} disabled={isReadOnly}
                        onChange={e => onChange && onChange(parseInt(e.target.value))} style={{ width: '100%' }} />
                </div>
            )}

            {field.type === 'bodychart' && (
                <BodyChartPreview
                    markers={Array.isArray(value) ? value : []}
                    viewLabel={field.viewLabel}
                    readOnly={isReadOnly}
                    onAddMarker={(m) => onChange && onChange([...(Array.isArray(value) ? value : []), m])}
                    onRemoveMarker={(idx) => onChange && onChange((Array.isArray(value) ? value : []).filter((_, i) => i !== idx))}
                />
            )}
        </div>
    );
}

/**
 * RefertoTemplatePreview
 *
 * Renderizza l'intero template in griglia, usato nel builder di ManageCenter.js
 * per mostrare un'anteprima live mentre si costruisce il modello.
 */
export function RefertoTemplatePreview({ fields = [], redFlagsOptions = [] }) {
    if (!fields.length) {
        return <div style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>Aggiungi campi per vedere l'anteprima.</div>;
    }
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', padding: '1rem', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            {fields.map(field => (
                <RefertoFieldRenderer key={field.id} field={field} allFields={fields} mode="preview" redFlagsOptions={redFlagsOptions} />
            ))}
        </div>
    );
}